import { useContext, useEffect, useCallback, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';

import { FEATURE } from '~/common/enums/feature.enum';
import { UserContext } from '~/components/providers/UserProvider';
import { Routes } from '~/domains/common/constants/routes';
import type { WelcomeModalHeaderType } from '~/domains/common/components/WelcomeModal/WelcomeModalHeader';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { isBrowserFn } from '~/utils/browser';


export function useWelcomeWidget() {
  const { user } = useContext(UserContext);
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const isWelcomeModalsEnabled = useIsFeatureEnabled(FEATURE.ENABLE_WELCOME_MODALS);

  const userBrand: WelcomeModalHeaderType = useMemo(() => {
    if (user?.rypUserId && user?.ofpUserId ) return 'combo';
    if (user?.rypUserId) return 'ryp';
    if (user?.ofpUserId) return 'ofp';
    return null;
  }, [user]);

  const cookieKey = useMemo(
    () => {
      if (!isBrowserFn() || !user?.id) return null;
      return `welcome_widget_shown_${user.id}`;
    },
    [user?.id]
  );

  const hasShownWelcomeWidget = Cookies.get(cookieKey) || localStorage.getItem(cookieKey);

  const shouldShowModal = useMemo(
    () => isWelcomeModalsEnabled && userBrand && !hasShownWelcomeWidget,
    [isWelcomeModalsEnabled, userBrand, hasShownWelcomeWidget]
  );

  const markWelcomeModalAsShown = useCallback(() => {
    setIsOpen(false);
    if (cookieKey) {
      // Storing both cookie and local storage to avoid issues with cookies being blocked by some browsers
      Cookies.set(cookieKey, 'true', { expires: 365 });
      localStorage.setItem(cookieKey, 'true');
    }
  }, [cookieKey]);

  const handleCreateContest = useCallback(() => {
    router.push(Routes.createAContest());
  }, [router]);

  useEffect(() => {
    if (shouldShowModal) {
      setIsOpen(true);
    }
  }, [shouldShowModal]);

  return {
    userBrand,
    isOpen,
    handleCloseWidget: markWelcomeModalAsShown,
    handleCreateContest,
  };
}
